import React, { useEffect, useState } from 'react';
import { Box, Typography, Tooltip, IconButton, Stack, Button } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import AttachmentIcon from '@mui/icons-material/Attachment';

import { ButtonUI, CheckboxUI, ConfirmDialogUI, LinearStaticProgressUI } from '../../Interface';
import productionService from '../../../service/production.service';
import JmEasyReasonDialog from './JmEasyReasonDialog';
import JmEasyMultiManDialog from './JmEasyMultiManDialog';
import JmEasyQuantityDialog from './JmEasyQuantityDialog';
import { NotificationType } from '../../../config';
import addNotification from '../../../utils/addNotification';
import colorFinder from '../../../utils/colorFinder';
import ProductionReceiptDialog from './ProductionReceiptDialog';
import jobmonitorEasyService from '../../../service/jobMonitorEasy.service';

dayjs.extend(relativeTime);
dayjs.extend(duration);

const InfoRow = ({ label, children, valueSx, mb = 1.5 }) => (
  <Typography fontSize={20} sx={{ mb, wordBreak: 'break-word' }}>
    <Box component="span" fontWeight="bold">
      {label}{' '}
    </Box>

    <Box component="span" sx={valueSx}>
      {children}
    </Box>
  </Typography>
);

const JmEasyJobmonitorCard = ({
  data,
  userId,
  setRefetch,
  rows,
  params,
  helperData,
  reasons,
  autoCompleteOptions,
  getAttachments,
  setConfirmDialogData,
  hideConfirmDialog,
}) => {
  const [isInterruptionDialogOpen, setIsInterruptionDialogOpen] = useState(false);
  const [isDowntimeDialogOpen, setIsDowntimeDialogOpen] = useState(false);
  const [isEmployeeGroupDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [isQuantityDialogOpen, setIsQuantityDialogOpen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(null);
  const [timer, setTimer] = useState(null);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedReasonCode, setSelectedReasonCode] = useState(null);
  const [disableBtn, setDisableBtn] = useState(false);

  const operation = rows?.find((op) => op.productionoperationid === data.productionoperationid);

  const reportingUserIds = data.reportingUserIds ? data.reportingUserIds.split(',').map(Number) : [];

  const isCurrentUserAttached = reportingUserIds.includes(Number(userId));

  const disableLeaveButton = reportingUserIds.length === 1 || !isCurrentUserAttached;

  useEffect(() => {
    if (data.productionreportStartDate) {
      if (timer) {
        clearInterval(timer);
      }

      const interval = setInterval(() => {
        const start = dayjs(data.productionreportStartDate);
        const now = dayjs();
        const diff = dayjs.duration(now.diff(start));

        setElapsedTime(
          `${diff.hours().toString().padStart(2, '0')}:${diff.minutes().toString().padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`
        );
      }, 1000);

      setTimer(interval);

      return () => clearInterval(interval);
    } else {
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
      setElapsedTime(null);
    }
  }, [data.productionreportStartDate]);

  const preparedness = data.prepQuantity ? Math.round((data.prepQuantity / data.productionQuantity) * 100) : 0;

  const checkOpenProductionReportByUser = ({ onConfirm, actionText }) => {
    hideConfirmDialog();
    jobmonitorEasyService.checkOpenProductionReportByUser(userId).then((data) => {
      if (data.hasOpenedReport) {
        setConfirmDialogData({
          open: true,
          message: actionText,
          buttons: [
            {
              label: 'Igen',
              variant: 'contained',
              color: '#27ca37',
              onClick: () => {
                onConfirm();
              },
            },
          ],
        });
      } else {
        onConfirm();
      }
    });
  };

  const handleStartOperation = () => {
    const values = {
      userId: userId,
      createdBy: userId,
      id: data.productionoperationid,
      employees: selectedEmployees,
    };
    setDisableBtn(true);
    jobmonitorEasyService
      .createProductionReport(values)
      .then(() => {
        setRefetch(true);
        setSelectedEmployees([]);
        setIsEmployeeDialogOpen(false);
        hideConfirmDialog();
      })
      .finally(() => setDisableBtn(false));
  };

  const handleStopOperation = (interruptionCode = null, downtimeCode = null, dataObj = {}, isContinuation = false) => {
    const values = {
      interruptionCode: interruptionCode || (selectedReasonCode?.trim() ? selectedReasonCode : null),
      downtimeCode,
      userId,
      quantities: dataObj,
      createNew: downtimeCode || isContinuation,
      id: data.productionreportId,
    };
    return productionService.updateProductionReport(values).then(() => {
      setIsDowntimeDialogOpen(false);
      hideConfirmDialog();
      setIsQuantityDialogOpen(false);
      setSelectedReasonCode('');
      setRefetch(true);
    });
  };

  const hasQuantity = (payload) => {
    const keys = Object.keys(payload || {});
    const numericKeys = keys.filter((k) => /^\d+$/.test(k));

    if (numericKeys.length > 0) {
      return numericKeys.every((k) => payload[k]?.quantity !== '');
    }

    return payload?.quantity !== '';
  };

  const handleFinishOperation = (values) => {
    if (!hasQuantity(values)) {
      addNotification({
        content: 'A mennyiség kitöltése kötelező!',
        type: NotificationType.ERROR,
      });
      return;
    }

    closeAllOpenedProductionReport({
      quantities: values,
      productionStatusId: 'COMPLETED',
      prodOperationId: data.productionoperationid,
      userId,
    });
  };

  const handleInterruptOperation = (interruptionCode = null, values) => {
    if (!hasQuantity(values)) {
      addNotification({
        content: 'A mennyiség kitöltése kötelező!',
        type: NotificationType.ERROR,
      });
      return;
    }

    closeAllOpenedProductionReport({
      interruptionCode: interruptionCode || (selectedReasonCode?.trim() ? selectedReasonCode : null),
      productionStatusId: 'INTERRUPTED',
      quantities: values,
      prodOperationId: data.productionoperationid,
      userId,
    });
  };

  const openConfirmDialog = () => {
    const interimReport = reasons.interimReport;

    if (data.productionreportStartDate && !isCurrentUserAttached) return;

    if (data.productionreportStartDate) {
      setConfirmDialogData({
        open: true,
        message: 'Mit szeretne csinálni?',
        buttons: [
          ...(!data.nonProductiveWork
            ? [
                {
                  label: 'Kész',
                  variant: 'contained',
                  color: '#27ca37',
                  onClick: () => {
                    hideConfirmDialog();
                    setIsQuantityDialogOpen(true);
                  },
                },
              ]
            : []),
          {
            label: 'Megszakít',
            variant: 'contained',
            color: '#f59e0b',
            onClick: () => {
              hideConfirmDialog();
              setIsInterruptionDialogOpen(true);
            },
          },
          ...(interimReport
            ? [
                {
                  label: interimReport.name,
                  variant: 'contained',
                  color: '#3bc3f5',
                  onClick: () => {
                    hideConfirmDialog();
                    setSelectedReasonCode(interimReport.code);
                    setIsQuantityDialogOpen(true);
                  },
                },
              ]
            : []),
        ],
      });
    } else {
      setConfirmDialogData({
        open: true,
        message: 'Biztosan el szeretné indítani a gyártást?',
        buttons: [
          {
            label: 'Igen',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => {
              checkOpenProductionReportByUser({
                onConfirm: handleStartOperation,
                actionText: 'Már van folyamatban lévő munkád. Megszakítod azt, hogy elindíthasd ezt?',
              });
            },
          },
        ],
      });
    }
  };

  const closeAllOpenedProductionReport = (data) => {
    jobmonitorEasyService.closeAllOpenedProductionReport(data).then(() => {
      setIsDowntimeDialogOpen(false);
      hideConfirmDialog();
      setIsQuantityDialogOpen(false);
      setSelectedReasonCode('');
      setRefetch(true);
    });
  };

  const handleJoinOperation = () => {
    jobmonitorEasyService.joinOperation(data.productionoperationid, userId).then(() => {
      setRefetch(true);
      hideConfirmDialog();
    });
  };

  const handleLeaveOperation = () => {
    jobmonitorEasyService.leaveOperation(data.productionoperationid, userId).then(() => {
      setRefetch(true);
      hideConfirmDialog();
    });
  };

  const isJoinableOperation = data.prodOpStatusId === 'INPROGRESS' && !isCurrentUserAttached;

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: '#E4F0FF',
        p: 0.5,
        mb: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: isJoinableOperation ? '#BCDBF7' : colorFinder(data.currentStatusColor)?.jobMonitorBgColor,
          borderRadius: 2,
          boxShadow:
            ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
          padding: 1,
          height: '100%',
          cursor: 'pointer',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#555',
          },

          '&:hover': {
            background: isJoinableOperation ? '#96c8f5' : colorFinder(data.currentStatusColor)?.jobMonitorHoverColor,
          },
        }}
        onClick={() => openConfirmDialog()}
      >
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          alignItems={{
            xs: 'stretch',
            lg: 'center',
          }}
          spacing={{
            xs: 1.5,
            lg: 2,
          }}
          mb={{
            xs: 1.5,
            lg: 3,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0} sx={{ order: { xs: 2, lg: 1 } }}>
            <Box
              width="fit-content"
              backgroundColor={
                isJoinableOperation ? '#78afdf' : colorFinder(data.currentStatusColor)?.jobMonitorChipColor
              }
              p={0.5}
              px={1.5}
              borderRadius={2}
            >
              <Typography fontWeight="bold" fontSize={20}>
                {data.productionOperationStatus}
              </Typography>
            </Box>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                getAttachments(data.productionid);
              }}
            >
              <AttachmentIcon fontSize="large" />
            </IconButton>
          </Stack>
          <Box flex={1} minWidth={0} sx={{ order: { xs: 3, lg: 2 } }}>
            <InfoRow label="Művelet:" mb={0}>
              {data.operationName}
            </InfoRow>
          </Box>
          {data.productionreportStartDate && (
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent={{
                xs: 'flex-start',
                sm: 'flex-end',
              }}
              gap={1}
              flexShrink={0}
              sx={{
                order: { xs: 1, lg: 3 },
                mb: { xs: '0.5rem !important', lg: '0rem !important' },
              }}
            >
              <Typography fontSize={20} lineHeight={1.2} sx={{ whiteSpace: 'nowrap', textWrap: 'nowrap', m: 0 }}>
                <Box component="span" fontWeight="bold">
                  Elindítva:{' '}
                </Box>
                <Box component="span">{dayjs(data.productionreportStartDate).format('YYYY-MM-DD HH:mm')}</Box>
              </Typography>

              <Typography
                color="blue"
                fontSize={20}
                lineHeight={1.2}
                sx={{
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {elapsedTime}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          alignItems={{
            xs: 'stretch',
            lg: 'center',
          }}
          spacing={1.5}
        >
          <Box flex={1} minWidth={0}>
            <InfoRow label="Megjegyzés:">{data.productionDescription}</InfoRow>
            <InfoRow label="Projekt:">{data.projectName}</InfoRow>
            <InfoRow label="Tervezett darabszám:">
              {data.productionQuantity} {data.articleQtyCode}
            </InfoRow>
            <InfoRow label="Cikknév:">{data.articleName}</InfoRow>
            <InfoRow
              label="Rájelentkezve:"
              valueSx={{
                color: '#1968cf',
                fontWeight: 'bold',
              }}
            >
              {data.reportingBy}
            </InfoRow>
            <InfoRow label="Folyamat:" mb={1.5} />
          </Box>
          {data.prodOpStatusId === 'INPROGRESS' && (
            <Stack
              direction={{
                xs: 'row',
                lg: 'column',
              }}
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Button
                variant="contained"
                disabled={isCurrentUserAttached}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialogData({
                    open: true,
                    message: 'Biztosan csatlakozik a gyártásra?',
                    buttons: [
                      {
                        label: 'Igen',
                        variant: 'contained',
                        color: '#27ca37',
                        onClick: () => {
                          checkOpenProductionReportByUser({
                            onConfirm: handleJoinOperation,
                            actionText: 'Már van folyamatban lévő munkád. Megszakítod azt, hogy elindíthasd ezt?',
                          });
                        },
                      },
                    ],
                  });
                }}
                sx={{
                  width: {
                    xs: 'fit-content',
                    sm: 250,
                  },
                  height: {
                    xs: 50,
                    sm: 100,
                  },
                  px: 2,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1.5rem',
                  },
                  bgcolor: '#f59e0b',
                  '&:hover': {
                    bgcolor: '#d97706',
                  },

                  '&.Mui-disabled': {
                    bgcolor: '#f59e0b',
                    color: 'white',
                    opacity: 0.5,
                  },
                }}
              >
                Csatlakozás
              </Button>

              <Button
                variant="contained"
                disabled={disableLeaveButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDialogData({
                    open: true,
                    message: 'Biztosan lecsatlakozik a gyártásról?',
                    buttons: [
                      {
                        label: 'Igen',
                        variant: 'contained',
                        color: '#27ca37',
                        onClick: () => {
                          handleLeaveOperation();
                        },
                      },
                    ],
                  });
                }}
                sx={{
                  width: {
                    xs: 'fit-content',
                    sm: 250,
                  },
                  height: {
                    xs: 50,
                    sm: 100,
                  },
                  px: 2,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1.5rem',
                  },
                  bgcolor: '#be123c',
                  '&:hover': {
                    bgcolor: '#9f1239',
                  },

                  '&.Mui-disabled': {
                    bgcolor: '#be123c',
                    color: 'white',
                    opacity: 0.5,
                  },
                }}
              >
                Lecsatlakozás
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
      <JmEasyReasonDialog
        open={isInterruptionDialogOpen}
        handleClose={() => setIsInterruptionDialogOpen(false)}
        data={reasons.interruption}
        mode="interrupt"
        setIsQuantityDialogOpen={setIsQuantityDialogOpen}
        setSelectedReasonCode={setSelectedReasonCode}
      />
      <JmEasyReasonDialog
        open={isDowntimeDialogOpen}
        handleClose={() => setIsDowntimeDialogOpen(false)}
        data={reasons.downtime}
        handleStopOperation={handleStopOperation}
        mode="downtime"
      />
      <JmEasyMultiManDialog
        open={isEmployeeGroupDialogOpen}
        handleClose={() => setIsEmployeeDialogOpen(false)}
        data={employeeGroups}
        setSelectedEmployees={setSelectedEmployees}
        handleStartOperation={handleStartOperation}
        disableBtn={disableBtn}
      />
      {!data?.stockAccounting ? (
        <JmEasyQuantityDialog
          open={isQuantityDialogOpen}
          handleClose={() => setIsQuantityDialogOpen(false)}
          handleFinishOperation={handleFinishOperation}
          handleInterruptOperation={handleInterruptOperation}
          mode={selectedReasonCode ? 'interrupt' : 'finish'}
          autoCompleteOptions={autoCompleteOptions}
          helperData={helperData}
          data={data}
        />
      ) : (
        <ProductionReceiptDialog
          open={isQuantityDialogOpen}
          onClose={() => setIsQuantityDialogOpen(false)}
          handleFinishOperation={handleFinishOperation}
          handleInterruptOperation={handleInterruptOperation}
          mode={selectedReasonCode ? 'interrupt' : 'finish'}
          onSave={(data) => {
            setIsQuantityDialogOpen(false);
          }}
          autocompleteData={helperData}
          cardData={data}
          autoCompleteOptions={autoCompleteOptions}
        />
      )}
    </Box>
  );
};

export default JmEasyJobmonitorCard;
